import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { RevisionRequestProps } from 'components/BlazeCommandLine/api/createRevisionRequest'
import { AIElement } from 'components/VisualStudioPlayground/Templates/Types'
import { GeneratePrompt, SuggestionRequest } from 'types'
import { reportError } from 'utils/error'

export enum OpenAIRole {
  'system' = 'system',
  'user' = 'user',
  'assistant' = 'assistant',
  'function' = 'function',
  'tool' = 'tool',
}

export type ToolCall = {
  type: 'function'
  function: {
    name: string
    description?: string
    parameters: {
      type: 'object'
      properties: Record<string, unknown>
      required?: string[]
    }
  }
}

export type ToolCallResponse = {
  id: string
  type: 'function'
  function: {
    name: string
    arguments: string
  }
}

export type OpenAIChoice = {
  index: number
} & (
  | {
      message: {
        role: OpenAIRole
        content: string
      }
      finish_reason: 'stop' | 'length'
    }
  | {
      message: {
        role: OpenAIRole
        content: null
        function_call: {
          name: string
          arguments: string
        }
      }
      finish_reason: 'function_call'
    }
  | {
      message: {
        role: OpenAIRole
        content: null
        tool_calls: Array<ToolCallResponse>
      }
      finish_reason: 'tool_calls'
    }
)

export type Gpt4Response = {
  id: string
  object: 'chat.completion'
  created: number
  model: string
  choices: OpenAIChoice[]
  usage: {
    prompt_tokens: number
    completion_tokens: number
    total_tokens: number
  }
  error?: {
    code: string
  }
}

export type GptPayloadMessage = {
  role: 'system' | 'user' | 'assistant'
  content: string | Array<{ type: 'text' | 'image_url'; image_url?: { url: string } | null; text?: string }>
}

export type GptToolFunctionCall = {
  type: string
  function: {
    name: string
    description?: string
    parameters: {
      type: 'object'
      properties: Record<string, unknown>
      required?: string[]
    }
  }
}

export type GptPayload = {
  model: string
  temperature?: number
  messages: Array<GptPayloadMessage>
  functions?: Array<{
    name: string
    description?: string
    parameters: {
      type: 'object'
      properties: Record<string, unknown>
      required?: string[]
    }
  }>
  tools?: Array<GptToolFunctionCall>
  max_tokens?: number
}

type JobStatus = { status: 'started' | 'processing' | 'failed' } | { status: 'done'; response: Gpt4Response }
type AIJobStatus<T> = { status: 'started' | 'processing' | 'failed' } | { status: 'done'; data: T }

type Options = {
  fallbackPayloads?: Array<GptPayload>
}

export class AiJobError extends Error {
  metadata?: object

  constructor(message: string, metadata?: object) {
    super(message)
    this.name = 'JobError'
    this.metadata = metadata
  }
}

export class AiMalformedResponseError extends Error {
  payload?: object

  constructor(message: string, payload?: object) {
    super(message)
    this.name = 'MalformedResponseError'
    this.payload = payload
  }
}

export class AIRateLimitResponseError extends Error {
  payload?: object

  constructor(message: string, payload?: object) {
    super(message)
    this.name = 'RateLimitError'
    this.payload = payload
  }
}

export type TitleGenerationPayload = {
  prompt: GeneratePrompt
  workspaceId: number
  count?: number
}

export type LinkGenerationPayload = {
  prompt: GeneratePrompt
  existingLinks: string[]
  workspaceId: number
  count?: number
}

export type SubjectLineGenerationPayload = {
  prompt: GeneratePrompt
  workspaceId: number
  count?: number
}

export type NewsletterSectionGenerationPayload = {
  prompt: GeneratePrompt
  workspaceId: number
  count?: number
}

export type SocialCaptionGenerationPayload = {
  prompt: GeneratePrompt
  workspaceId: number
  count?: number
}

export type OutlineGenerationPayload = {
  prompt: GeneratePrompt
  workspaceId: number
  title: string
}

export type IntroductionGenerationPayload = {
  prompt: GeneratePrompt
  workspaceId: number
  title: string
}

export enum VisualCompletionType {
  Image = 'image',
  Text = 'text',
  Style = 'style',
}

export const AiClient = {
  chat: async (gptPayload: GptPayload, signal?: AbortSignal, opts?: Options): Promise<Gpt4Response> => {
    const jobIDResponse = await sendToApi<string>(`/extract-brand-voice`, {
      method: 'POST',
      body: gptPayload,
      signal,
    })

    if (jobIDResponse.type !== SUCCESS_RESPONSE_TYPE) {
      throw new AiJobError('Unable to create backend job')
    }

    const jobID = jobIDResponse.data
    let status: JobStatus
    let gpt4response: Gpt4Response | undefined

    do {
      const jobStatusResponse = await sendToApi<JobStatus>(`/extract-brand-voice?job_id=${jobID}`, { signal })
      if (jobStatusResponse.type !== SUCCESS_RESPONSE_TYPE) {
        throw new AiJobError('Unable to get backend job status')
      }
      status = jobStatusResponse.data
      if (status.status === 'done') {
        gpt4response = status.response
        break
      }
      await new Promise((resolve) => setTimeout(resolve, 500))
    } while (!['done', 'failed'].includes(status?.status))

    if (status.status === 'failed') {
      throw new AiJobError('Backend job status reports "failed"', status)
    }

    if (
      gpt4response &&
      gpt4response.error?.code === 'rate_limit_exceeded' &&
      opts?.fallbackPayloads &&
      opts.fallbackPayloads.length > 0
    ) {
      reportError(new AIRateLimitResponseError('AI Client received a rate_limit_exceeded response'), {
        severity: 'warning',
        metaData: {
          info: `error: ${gpt4response}`,
        },
      })

      const [payload, ...fallbackPayloads] = opts.fallbackPayloads
      return await AiClient.chat(payload, signal, { ...opts, fallbackPayloads })
    }

    if (!gpt4response || 'error' in gpt4response || !('choices' in gpt4response)) {
      throw new AiMalformedResponseError('Malformed response', gpt4response)
    }

    return gpt4response
  },
  analyzeIntent: ({ prompt, documentAsMarkdown }: { prompt: string; documentAsMarkdown?: string }) => {
    return sendToApi<{ intent: string }>(`/analyze-intent`, {
      method: 'POST',
      body: {
        prompt,
        document_as_markdown: documentAsMarkdown,
      },
    })
  },
  analyzeVisualPrompt: ({ prompt, base64screenshot }: { prompt: string; base64screenshot: string }) => {
    return sendToApi<Array<ToolCallResponse>>(`/analyze-visual-prompt`, {
      method: 'POST',
      body: {
        prompt,
        image: base64screenshot,
      },
    })
  },
  templateAnalysis: ({ aiElements, url }: { aiElements: Array<AIElement>; url: string }) => {
    return sendToApi<string>(`/template-analysis`, {
      method: 'POST',
      body: {
        ai_elements: aiElements,
        url,
      },
    })
  },
  templateCompletion: ({
    designId,
    prompt: {
      revisionType: revision_type,
      customPrompt: custom_prompt,
      image,
      completionTypes: completion_types,
      brandVoiceId: brand_voice_id,
      tones,
      referenceText: reference_text,
      language,
    },
    selection,
  }: RevisionRequestProps) => {
    return sendToApi<{ request: SuggestionRequest }>(`/template-completion`, {
      method: 'POST',
      body: {
        design_id: designId,
        prompt: {
          revision_type,
          custom_prompt,
          image,
          completion_types,
          brand_voice_id,
          tones,
          reference_text,
          language,
        },
        selection,
      },
    })
  },
  captionGeneration: ({
    prompt,
    brandVoiceId,
    tones,
    language,
    externalIntegrationId,
  }: {
    prompt: string
    brandVoiceId?: number
    tones?: string[]
    language?: string
    externalIntegrationId?: number
  }) => {
    return sendToApi<string>(`/caption-generation`, {
      method: 'POST',
      body: {
        prompt,
        brand_voice_id: brandVoiceId,
        tones,
        language,
        external_integration_id: externalIntegrationId,
      },
    })
  },
  generateTitles: async ({ payload, signal }: { payload: TitleGenerationPayload; signal?: AbortSignal }) => {
    const { prompt, workspaceId, count } = payload
    const jobIDResponse = await sendToApi<string>(`/w/${workspaceId}/generate_titles`, {
      method: 'POST',
      body: {
        prompt,
        count,
      },
    })

    if (jobIDResponse.type !== SUCCESS_RESPONSE_TYPE) {
      throw new AiJobError('Unable to create backend job')
    }

    const titlesResponse = await pollAiJob<string>(jobIDResponse.data, signal)
    return titlesResponse
  },
  generateLinks: async ({ payload, signal }: { payload: LinkGenerationPayload; signal?: AbortSignal }) => {
    const { prompt, existingLinks, workspaceId, count } = payload
    const jobIDResponse = await sendToApi<string>(`/w/${workspaceId}/generate_links`, {
      method: 'POST',
      body: {
        prompt,
        existing_links: existingLinks,
        count,
      },
    })

    if (jobIDResponse.type !== SUCCESS_RESPONSE_TYPE) {
      throw new AiJobError('Unable to create backend job')
    }

    const linksResponse = await pollAiJob<string>(jobIDResponse.data, signal)
    return linksResponse
  },
  generateSubjectLines: async ({
    payload,
    signal,
  }: {
    payload: SubjectLineGenerationPayload
    signal?: AbortSignal
  }) => {
    const { prompt, workspaceId, count } = payload
    const jobIDResponse = await sendToApi<string>(`/w/${workspaceId}/generate_subject_lines`, {
      method: 'POST',
      body: {
        prompt,
        count,
      },
    })

    if (jobIDResponse.type !== SUCCESS_RESPONSE_TYPE) {
      throw new AiJobError('Unable to create backend job')
    }

    const subjectLinesResponse = await pollAiJob<string>(jobIDResponse.data, signal)
    return subjectLinesResponse
  },
  generateNewsletterSections: async ({
    payload,
    signal,
  }: {
    payload: NewsletterSectionGenerationPayload
    signal?: AbortSignal
  }) => {
    const { prompt, workspaceId, count } = payload
    const jobIDResponse = await sendToApi<string>(`/w/${workspaceId}/generate_sections`, {
      method: 'POST',
      body: {
        prompt,
        count,
      },
    })

    if (jobIDResponse.type !== SUCCESS_RESPONSE_TYPE) {
      throw new AiJobError('Unable to create backend job')
    }

    const sectionsResponse = await pollAiJob<string>(jobIDResponse.data, signal)
    return sectionsResponse
  },
  generateSocialCaptions: async ({
    payload,
    signal,
  }: {
    payload: SocialCaptionGenerationPayload
    signal?: AbortSignal
  }) => {
    const { prompt, workspaceId, count } = payload
    const jobIDResponse = await sendToApi<string>(`/w/${workspaceId}/generate_captions`, {
      method: 'POST',
      body: {
        prompt,
        count,
      },
    })

    if (jobIDResponse.type !== SUCCESS_RESPONSE_TYPE) {
      throw new AiJobError('Unable to create backend job')
    }

    const socialCaptionsResponse = await pollAiJob<string>(jobIDResponse.data, signal)
    return socialCaptionsResponse
  },
  generateOutline: async ({ payload, signal }: { payload: OutlineGenerationPayload; signal?: AbortSignal }) => {
    const { prompt, workspaceId, title } = payload
    const jobIDResponse = await sendToApi<string>(`/w/${workspaceId}/generate_outline`, {
      method: 'POST',
      body: {
        prompt,
        title,
      },
    })

    if (jobIDResponse.type !== SUCCESS_RESPONSE_TYPE) {
      throw new AiJobError('Unable to create backend job')
    }

    const outlineResponse = await pollAiJob<string>(jobIDResponse.data, signal)
    return outlineResponse
  },
  generateIntroduction: async ({
    payload,
    signal,
  }: {
    payload: IntroductionGenerationPayload
    signal?: AbortSignal
  }) => {
    const { prompt, workspaceId, title } = payload
    const jobIDResponse = await sendToApi<string>(`/w/${workspaceId}/generate_introduction`, {
      method: 'POST',
      body: {
        prompt,
        title,
      },
    })

    if (jobIDResponse.type !== SUCCESS_RESPONSE_TYPE) {
      throw new AiJobError('Unable to create backend job')
    }

    const introductionResponse = await pollAiJob<string>(jobIDResponse.data, signal)
    return introductionResponse
  },
}

export const pollAiJob = async <T>(jobID: string, signal?: AbortSignal) => {
  let status: AIJobStatus<T>
  let response: T | undefined
  let retries = 5

  await new Promise((resolve) => setTimeout(resolve, 1000))
  do {
    retries--
    const jobStatusResponse = await sendToApi<AIJobStatus<T>>(`/ai-job-status?job_id=${jobID}`, { signal })
    if (jobStatusResponse.type !== SUCCESS_RESPONSE_TYPE && retries <= 0) {
      throw new AiJobError('Unable to get backend job status')
    }
    status = jobStatusResponse.data
    if (status.status === 'done') {
      response = status.data
      break
    }
    await new Promise((resolve) => setTimeout(resolve, 500))
  } while (!['done', 'failed'].includes(status?.status) || retries > 0)

  if (status.status === 'failed') {
    throw new AiJobError('Backend job status reports "failed"', status)
  }

  if (!response) {
    throw new AiMalformedResponseError('Malformed response', { response })
  }

  return response
}
